import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Stack, Typography, IconButton, Card, TextField, InputAdornment } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import MDButton from 'components/MDButton';
import EditIcon from "common/customIcons/edit";
import DeleteIcon from "common/customIcons/delete";
import { useNavigate } from 'react-router-dom';
import { deleteBrbGraphicsById, fetchBRBList } from '../../../../redux/slices/hyperwolf/settings';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useDebounce } from 'hooks/useDebounce';
import { AgGridTable } from 'components/tables/AgGridTable';
import defaultImage from 'assets/images/placeholder-image.png';
import SearchIcon from 'common/customIcons/search';
import { CloseRounded } from '@mui/icons-material';
import { closeCustomModalDialog, openCustomModalDialog, setModalLoader } from '../../../../redux/slices/modalSlice';
import { openSnackbar } from '../../../../redux/slices/common';
import { hasSectionPermission } from 'utilities/common';
import { getPermissionsByMode } from 'utilities/common';
import { getData } from 'utilities/common';

const BrbHome = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { loading, brbData } = useSelector(state => state.hyperwolfSettings);
  const searchTermRef = useRef('');
  const mainGridApiRef = useRef('');
  const gridRefApi = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const pageRef = useRef(0);
  const { user } = useSelector(state => state.auth);

  let localUser = getData('login-user-info')
  if (localUser) {
    localUser = JSON.parse(localUser)
  }

  const adminMode = localStorage.getItem("adminsMode") ? JSON.parse(localStorage.getItem("adminsMode")) : null;
  const permissionsForMode = getPermissionsByMode(localUser?.permissions, adminMode);

  const canCreate = hasSectionPermission(permissionsForMode, "Settings", "Create");
  const canEdit = hasSectionPermission(permissionsForMode, "Settings", "Edit/Update");
  const canView = hasSectionPermission(permissionsForMode, "Settings", "View Only");
  const canDelete = hasSectionPermission(permissionsForMode, "Settings", "Delete");

  const columnDefs = [
    {
      headerName: 'BRB  Banner For Desktop',
      field: 'desktopImages',
      suppressMenu: false,
      key: 'desktopImages',
      minWidth: 150,
      cellRenderer: useCallback((props) => (
        props?.value?.imageUrl ? (
          props?.value?.mediaType === 'image' ? (
            <img style={{ borderRadius: "6px", objectFit: "cover", overflow: "hidden" }} width="100px" height="50px" src={props?.value?.imageUrl || defaultImage} alt={props?.value?.altText || ''} />
          ) : (
            <video
              controls
              style={{
                width: '100px',
                height: '50px',
                borderRadius: '6px',
                objectFit: 'cover',
                overflow: 'hidden'
              }}
            >
              <source src={props?.value?.imageUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          )
        ) : '-'
      ), []),
      sortable: false,

    },

    {
      headerName: 'BRB  Banner For Mobile',
      field: 'mobileImages',
      minWidth: 150,
      cellRenderer: useCallback((props) => (
        props?.value?.imageUrl ? (
          props?.value?.mediaType === 'image' ? (
            <img style={{ borderRadius: "6px", objectFit: "cover", overflow: "hidden" }} width="100px" height="50px" src={props?.value?.imageUrl || defaultImage} alt={props?.value?.altText || ''} />
          ) : (
            <video
              controls
              style={{
                width: "100px",
                height: "50px",
                borderRadius: "6px",
                objectFit: "cover",
                overflow: "hidden"
              }}
            >
              <source src={props?.value?.imageUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          )
        ) : '-'
      ), []),
      flex: 1.3,
      sortable: false
    },
    {
      headerName: 'Region Name',
      field: 'regionName',
      minWidth: 150,
      cellRenderer: (props) => {
        return props?.value ? `${props?.value}` : '-';
      },
      flex: 1.3,
      sortable: false
    },

    {
      headerName: 'Action',
      field: 'createdDate',
      headerClass: 'right-aligned-cell-header',
      minWidth: 150,
      cellRenderer: useCallback((props) => (
        props?.value ? <Box display="flex" alignItems="center" justifyContent="flex-end" className="m-0">
          <IconButton
            disabled={!canEdit}
            color="primary"
            size="small"
            variant="primary"
            onClick={(e) => { e.stopPropagation(); navigate(`/hyperwolf/banner-manage/brb-edit/${props?.data?._id}`) }}
          >
            <EditIcon width="16px" height="16px" />
          </IconButton>
          <IconButton
            disabled={!canDelete}
            color="error"
            size="small"
            variant="primary"
            sx={{ marginLeft: 1 }}
            onClick={() =>
              dispatch(openCustomModalDialog({ modalData: { deleteHandler, deleteId: props?.data?._id }, modal: 'DELETE_MODAL' }))
            }
          >
            <DeleteIcon width="16px" height="16px" />
          </IconButton>
        </Box> : '-'
      ), []),
      flex: 1.3,
      sortable: false,
      cellStyle: { textAlign: 'right', justifyContent: 'end' },

    },
  ];

  // Get the first object where defaultImage is not true
  const singleDefaultObject = brbData?.brb.find(item => item.defaultImage === true);

  const resetData = () => {
    pageRef.current = 0;
    if (mainGridApiRef.current) {
      mainGridApiRef.current.forEachNode((node) => {
        node.setSelected(false);
      });
    }
    searchFn();
  };

  const deleteHandler = (id) => {
    const payload = {
      updatedBy: user?.name,
    }
    dispatch(setModalLoader(true));
    dispatch(deleteBrbGraphicsById({ id, payload })).unwrap().then((res) => {
      showError(res?.data?.message, 'success');
      resetData();
      pageRef.current = 0;
      getRows(gridRefApi.current);
      dispatch(closeCustomModalDialog());
    }).catch((err) => {
      dispatch(setModalLoader(false));
      showError(err?.message, 'error');
    });

  };

  const showError = (message, errortype) => {
    if (errortype === 'success') {
      dispatch(openSnackbar({ content: message, color: 'success', icon: 'check' }));
    }
    if (errortype === 'error') {
      dispatch(openSnackbar({ content: message, color: 'error', icon: 'warning' }));
    }
  };

  const getRows = async (params) => {
    gridRefApi.current = params;
    try {
      const res = await dispatch(fetchBRBList({ skip: pageRef.current, limit: 20, searchTerm: searchTermRef.current })).unwrap();
      const { brb = [], total = 0 } = res;
      // Filter out objects where defaultImage is true
      const filteredBRB = brb.filter(item => !item.defaultImage);
      if (filteredBRB.length === 0) {
        // No non-default banners → show empty table and stop pagination
        params.successCallback([], 0);
        mainGridApiRef.current?.showNoRowsOverlay();
        return;
      }
      // Normal data flow
      const filteredTotal = total - (brb.length - filteredBRB.length);
      params.successCallback(filteredBRB, filteredTotal);
      pageRef.current += 20;

    } catch (error) {
      params.successCallback([], 0);
    }
  };

  const searchHandler = (searchValue) => {
    setSearchTerm(searchValue);
    searchTermRef.current = searchValue;
    handleSearch(searchValue);
    // Debounce the search callback
  };

  const handleSearch = useDebounce(() => {
    pageRef.current = 0;
    searchFn();
  }, 500);

  const searchFn = (value) => {
    const dataSource = {
      getRows: (params) => getRows(params, value) // Pass the current filter payload
    };
    if (mainGridApiRef.current) {
      mainGridApiRef.current.ensureIndexVisible(0, null);
      mainGridApiRef.current.setGridOption("datasource", dataSource);
    }
  };

  useEffect(() => {
    if (!mainGridApiRef.current) return;
    if (loading) mainGridApiRef.current.showLoadingOverlay();
    else if (!brbData?.brb?.some(item => !item.defaultImage)) mainGridApiRef.current.showNoRowsOverlay();
    else mainGridApiRef.current.hideOverlay();
  }, [brbData, loading]);

  return (
    <Box>
      <Stack direction='row' justifyContent='space-between' alignItems='center' mb={2.5} className="centralize-banner-top">
        <Typography variant='h4' style={{ display: "flex", alignItems: "center", gap: "5px" }}>Express Menu</Typography>

        <Stack direction="row" justifyContent={{ xs: 'start', sm: 'end' }} flexWrap="wrap" alignItems="stretch" gap={2} className='search-height'>
          <TextField
            className="custom-search-field"
            placeholder="Search"
            label=""
            value={searchTerm}
            onChange={(e) => searchHandler(e.target.value)}
            sx={{ minWidth: "280px" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton size="small">
                    <SearchIcon width="18px" height="18px" />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment:
                searchTerm?.length > 0 ? (
                  <InputAdornment>
                    <IconButton onClick={() => searchHandler("")} size="small">
                      <CloseRounded />
                    </IconButton>
                  </InputAdornment>
                ) : null,
            }}
            size='small'
          />
          {
            canView &&
            <MDButton
              variant="contained" color="primary"
              sx={{ minWidth: '160px', backgroundColor: '#2E2E3A !important', boxShadow: "none !important" }}
              style={{ textTransform: 'none', minHeight: '36px', padding: "8px 20px" }}
              onClick={() => navigate("/hyperwolf/banner-manage/brb-activity-logs")}
            >
              Activity Logs
            </MDButton>
          }
          {
            canCreate &&
            <MDButton
              startIcon={<AddRoundedIcon fontSize='large' />}
              variant="contained" color="primary"
              sx={{ minWidth: '160px', boxShadow: "none !important" }}
              style={{ textTransform: 'none', minHeight: '36px', padding: "8px 20px" }}
              onClick={() => navigate("/hyperwolf/banner-manage/brb-add")}
            >
              Add Shop Closed Graphic
            </MDButton>
          }
        </Stack>
      </Stack>
      <Box className="centralize-banner-con">
        {
          canView &&
          <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 2.5, boxShadow: 'none' }}>
            <Typography variant='body2' fontWeight="semibold" color="#656575" mb={1.5}>
              Default BRB Banner for all regions
            </Typography>

            <Box display="flex" alignItems="center" gap={3}>
              <Box display="flex" alignItems="center" gap={2}>
                <Box display="flex" flexDirection="column" gap={0.5}>
                  <Typography variant="body2" fontWeight="medium">
                    Desktop
                  </Typography>
                  {
                    singleDefaultObject?.desktopImages?.mediaType === 'video' ?
                      <video
                        style={{
                          width: "200px",
                          height: "120px",
                          borderRadius: "6px",
                          objectFit: "cover",
                          overflow: "hidden"
                        }}
                        controls
                      >
                        <source src={singleDefaultObject?.desktopImages?.imageUrl} type="video/mp4" />
                      </video>
                      :
                      <img alt={singleDefaultObject?.desktopImages?.altText} style={{ width: "200px", height: "120px", objectFit: "cover", borderRadius: "6px", overflow: "hidden" }} src={singleDefaultObject?.desktopImages?.imageUrl || defaultImage} />
                  }
                </Box>
                <Box display="flex" flexDirection="column" gap={0.5}>
                  <Typography variant="body2" fontWeight="medium">
                    Mobile
                  </Typography>
                  {
                    singleDefaultObject?.mobileImages?.mediaType === 'video' ?
                      <video
                        style={{
                          width: '200px',
                          height: '120px',
                          objectFit: 'cover',
                          borderRadius: '6px',
                          overflow: 'hidden',
                        }}
                        controls
                      >
                        <source src={singleDefaultObject?.mobileImages?.imageUrl} type="video/mp4" />
                      </video>
                      :
                      <img alt={singleDefaultObject?.mobileImages?.altText} style={{ width: "200px", height: "120px", objectFit: "cover", borderRadius: "6px", overflow: "hidden" }} src={singleDefaultObject?.mobileImages?.imageUrl || defaultImage} />
                  }
                </Box>
              </Box>

              {
                canEdit &&
                <Box display="flex" alignItems="center" justifyContent="flex-end" className="m-0">
                  <IconButton
                    color="primary"
                    size="small"
                    variant="primary"
                    onClick={(e) => { e.stopPropagation(); navigate(`/hyperwolf/banner-manage/brb-edit/${singleDefaultObject?._id}`) }}
                  >
                    <EditIcon width="16px" height="16px" />
                  </IconButton>
                </Box>
              }
            </Box>
            {
              canView &&
              <Box className="centralize-banner-con table-nodata-size" style={{ borderTop: "1px solid #F1F1F1", paddingTop: "30px", marginTop: "30px" }}>
                <Typography variant='body2' fontWeight="semibold" color="#656575" mb={1.5}>BRB Banner for specific region</Typography>
                <AgGridTable col={columnDefs} getRows={getRows} mainGridApiRef={mainGridApiRef} pageRef={pageRef} isLoading={loading} style={{ height: '70vh' }} />
              </Box>
            }

          </Card>
        }
      </Box>
    </Box>
  )
}

export default BrbHome